import React, { createContext, useContext, ReactNode, useState, useCallback, useMemo, useEffect, useRef } from 'react';

// Global subscription manager (outside of React context)
class RefreshStateManager {
  private refreshStates: Record<string, number> = {};
  private subscribers: Record<string, Set<(value: number) => void>> = {};

  triggerRefresh(documentId: string): void {
    const newValue = (this.refreshStates[documentId] || 0) + 1;
    this.refreshStates[documentId] = newValue;

    // Notify only subscribers for this specific document
    const documentSubscribers = this.subscribers[documentId];
    if (documentSubscribers) {
      documentSubscribers.forEach(callback => callback(newValue));
    }
  }

  resetRefreshState(documentId: string): void {
    delete this.refreshStates[documentId];

    // Notify subscribers that this document was reset
    const documentSubscribers = this.subscribers[documentId];
    if (documentSubscribers) {
      documentSubscribers.forEach(callback => callback(0));
    }
  }

  subscribeToDocument(documentId: string, callback: (value: number) => void): () => void {
    if (!this.subscribers[documentId]) {
      this.subscribers[documentId] = new Set();
    }
    this.subscribers[documentId].add(callback);

    // Immediately call callback with current value
    const currentValue = this.refreshStates[documentId] || 0;
    callback(currentValue);

    // Return unsubscribe function
    return () => {
      this.subscribers[documentId]?.delete(callback);
      if (this.subscribers[documentId]?.size === 0) {
        delete this.subscribers[documentId];
      }
    };
  }

  getRefreshState(documentId: string): number {
    return this.refreshStates[documentId] || 0;
  }
}

// Global instance
const refreshStateManager = new RefreshStateManager();

// Split into two contexts - Actions (stable) and States (changes)
interface RefreshActionsContextValue {
  triggerRefresh: (documentId: string) => void;
  resetRefreshState: (documentId: string) => void;
}

interface RefreshStatesContextValue {
  refreshStates: Record<string, number>;
  getRefreshState: (documentId: string) => number;
}

const RefreshActionsContext = createContext<RefreshActionsContextValue | null>(null);
const RefreshStatesContext = createContext<RefreshStatesContextValue | null>(null);

interface RefreshStatesProviderProps {
  children: ReactNode;
}

export const RefreshStatesProvider: React.FC<RefreshStatesProviderProps> = ({
  children
}) => {
  const [refreshStates, setRefreshStates] = useState<Record<string, number>>({});

  // Actions - these are stable and delegate to global manager
  const triggerRefresh = useCallback((documentId: string) => {
    refreshStateManager.triggerRefresh(documentId);
    // Also update local state for legacy compatibility
    setRefreshStates(prev => ({
      ...prev,
      [documentId]: (prev[documentId] || 0) + 1
    }));
  }, []);

  const resetRefreshState = useCallback((documentId: string) => {
    refreshStateManager.resetRefreshState(documentId);
    // Also update local state for legacy compatibility
    setRefreshStates(prev => {
      const { [documentId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  // Memoize actions context value (stable, never changes)
  const actionsValue = useMemo(() => ({
    triggerRefresh,
    resetRefreshState
  }), [triggerRefresh, resetRefreshState]);

  // States context value (for legacy compatibility)
  const getRefreshState = useCallback((documentId: string): number => {
    return refreshStates[documentId] || 0;
  }, [refreshStates]);

  const statesValue = useMemo(() => ({
    refreshStates,
    getRefreshState
  }), [refreshStates, getRefreshState]);

  return (
    <RefreshActionsContext.Provider value={actionsValue}>
      <RefreshStatesContext.Provider value={statesValue}>
        {children}
      </RefreshStatesContext.Provider>
    </RefreshActionsContext.Provider>
  );
};

// Hook for actions only (won't cause rerenders when states change)
export const useRefreshActions = () => {
  const context = useContext(RefreshActionsContext);
  if (!context) {
    throw new Error('useRefreshActions must be used within RefreshStatesProvider');
  }
  return context;
};

// Hook for states only (will cause rerenders when states change)
export const useRefreshStates = () => {
  const context = useContext(RefreshStatesContext);
  if (!context) {
    throw new Error('useRefreshStates must be used within RefreshStatesProvider');
  }
  return context;
};

// Backward compatibility hook (deprecated - use specific hooks above)
export const useRefreshStatesContext = () => {
  const actions = useRefreshActions();
  const states = useRefreshStates();
  return { ...actions, ...states };
};

// Hook to get refresh state for a specific document (TRULY optimized - only rerenders when that specific document changes)
export const useDocumentRefreshState = (documentId: string): number => {
  const [documentRefreshState, setDocumentRefreshState] = useState<number>(0);

  // Subscribe to changes for this specific document only using global manager
  useEffect(() => {
    if (documentId === null || documentId === undefined) return;
    const unsubscribe = refreshStateManager.subscribeToDocument(documentId, (newValue) => {
      setDocumentRefreshState(newValue);
    });

    return unsubscribe;
  }, [documentId]);

  return documentRefreshState;
};
